import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { rateGroupAPI, billingRateAPI } from '../services/api';
import type { RateGroup, BillingRate } from '../types';

const RateGroupForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [billingRates, setBillingRates] = useState<BillingRate[]>([]);
  const [selectedRates, setSelectedRates] = useState<number[]>([]);
  const [formData, setFormData] = useState<Partial<RateGroup>>({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchBillingRates();
    if (isEditMode && id) {
      fetchRateGroup(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchBillingRates = async () => {
    try {
      const response = await billingRateAPI.getAll();
      setBillingRates(response.data.results.filter(r => r.is_active));
    } catch (error) {
      console.error('Failed to fetch billing rates:', error);
    }
  };

  const fetchRateGroup = async (groupId: number) => {
    try {
      const response = await rateGroupAPI.getById(groupId);
      setFormData({
        name: response.data.name,
        description: response.data.description,
        is_active: response.data.is_active,
      });
      
      const rateIds = response.data.rate_group_memberships?.map(m => m.billing_rate) || [];
      setSelectedRates(rateIds);
    } catch (error) {
      console.error('Failed to fetch rate group:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dataToSave = {
        ...formData,
        billing_rates: selectedRates,
      };

      if (isEditMode && id) {
        await rateGroupAPI.update(parseInt(id), dataToSave);
      } else {
        await rateGroupAPI.create(dataToSave);
      }
      navigate('/rate-groups');
    } catch (error) {
      console.error('Failed to save rate group:', error);
      alert('Failed to save rate group');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRateToggle = (rateId: number) => {
    setSelectedRates(prev => 
      prev.includes(rateId) 
        ? prev.filter(id => id !== rateId)
        : [...prev, rateId]
    );
  };

  return (
    <div className="container">
      <div className="card">
        <h2>{isEditMode ? 'Edit Rate Group' : 'Create New Rate Group'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Rate Group Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Standard Rates, PTO Rates"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Description of this rate group"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ cursor: 'pointer' }}
              />
              <span>Active</span>
            </label>
          </div>

          <div className="form-group">
            <label>Select Billing Rates ({selectedRates.length} selected)</label>
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              padding: '1rem',
              maxHeight: '400px',
              overflowY: 'auto',
              backgroundColor: '#f8f9fa'
            }}>
              {billingRates.length === 0 ? (
                <p style={{ color: '#7f8c8d', margin: 0 }}>No billing rates available</p>
              ) : (
                billingRates.map((rate) => (
                  <div 
                    key={rate.id} 
                    style={{ 
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: selectedRates.includes(rate.id!) ? '2px solid #3498db' : '1px solid #ddd',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleRateToggle(rate.id!)}
                  >
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        checked={selectedRates.includes(rate.id!)}
                        onChange={() => {}}
                        style={{ cursor: 'pointer', marginTop: '0.25rem', width: '16px', height: '16px', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold' }}>
                          {rate.project_code} - {rate.name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                          {rate.project_name} • ${rate.rate_amount}/hr
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update Rate Group' : 'Create Rate Group'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/rate-groups')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RateGroupForm;

